import { injectable } from 'inversify';
import { createSelector } from '@reduxjs/toolkit';
import { useAppSelector } from '../../state/hooks';
import {
  AssessmentRepository,
  IncidentRepository,
  PlayerRepository,
} from '../../state/reduxRepositories';
import { Player } from '../../state/reduxRepositories/players/player.interfaces';
import { Incident } from '../../state/reduxRepositories/incident/incident.interface';
import { ViewIncidentQueryService } from './viewIncident.queryParam.service';
import { Assessment } from '../../state/reduxRepositories/assessments/assessments.interfaces';
import clinicAssessmentSpecs from '../../clinicApp/pages/assessment/takeAssessment/md_portal_clearance_screen.json';
import gpClearanceAssessmentSpecs from '../../guardianApp/components/gpClearance/gp_clearance_spec.json';
import { NextIncident } from '../../helpers/nextIncident';

@injectable()
export class ViewIncidentDomainService {
  currentPlayerSelector: any;
  currentIncidentSelector: any;
  currentIncidentByPlayerSelector: any;
  currentClearanceAssessmentByPlayerSelector: any;
  currentIncidentStatusSelector: any;

  public constructor(
    private playerRepository: PlayerRepository,
    private incidentRepository: IncidentRepository,
    private assessmentRepository: AssessmentRepository,
    private viewIncidentQueryService: ViewIncidentQueryService,
  ) {
    this.createCurrentPlayerSelector();
    this.createCurrentIncidentSelector();
    this.createCurrentIncidentByPlayerSelector();
    this.createClearanceAssessmentByPlayerSelector();
    this.createCurrentIncidentStatusSelector();
  }

  useSelectedIncidentSelector = (): Incident => {
    return useAppSelector(this.currentIncidentSelector);
  };

  useSelectedClearanceAssessmentSelector = (): Assessment[] => {
    return useAppSelector(this.currentClearanceAssessmentByPlayerSelector);
  };

  useSelectedPlayerSelector = (): Player => {
    return useAppSelector(this.currentPlayerSelector);
  };

  useSelectedIncidentByPlayerSelector = (): Incident[] => {
    return useAppSelector(this.currentIncidentByPlayerSelector);
  };

  useCurrentIncidentStatusSelector = (): string => {
    return useAppSelector(this.currentIncidentStatusSelector);
  };

  private createCurrentIncidentStatusSelector = () => {
    this.currentIncidentStatusSelector = createSelector(
      this.currentIncidentSelector,
      this.currentPlayerSelector,
      (selectedIncident: Incident, selectedPlayer: Player) => {
        const isWaiting =
          !selectedPlayer?.clearedToPlay &&
          new Date(selectedIncident?.incidentDateTime).getTime() >=
            new Date(
              selectedPlayer?.notClearedToPlayUpdatedAt as string,
            ).getTime();
        return isWaiting ? 'waiting' : 'cleared';
      },
    );
  };

  private createCurrentIncidentSelector = () => {
    this.currentIncidentSelector = createSelector(
      this.viewIncidentQueryService.mergedQuerySelector,
      this.incidentRepository.selectors.resourcesSelector,
      (queryParams, incidents: Incident[]) => {
        return incidents.find(
          (incident: Incident) =>
            incident.id === queryParams.selectedIncidentId,
        );
      },
    );
  };

  private createCurrentPlayerSelector = () => {
    this.currentPlayerSelector = createSelector(
      this.viewIncidentQueryService.mergedQuerySelector,
      this.playerRepository.selectors.resourcesSelector,
      (queryParams, players: Player[]) => {
        return (
          players.find((a: Player) => a.id === queryParams.selectedPlayerId) ||
          ({} as Player)
        );
      },
    );
  };

  private createCurrentIncidentByPlayerSelector = () => {
    this.currentIncidentByPlayerSelector = createSelector(
      this.viewIncidentQueryService.mergedQuerySelector,
      this.incidentRepository.selectors.resourcesSelector,
      (queryParam, incidents: Incident[]) => {
        return incidents.filter(
          (a: Incident) => a.playerId === queryParam.selectedPlayerId,
        );
      },
    );
  };

  private createClearanceAssessmentByPlayerSelector = () => {
    this.currentClearanceAssessmentByPlayerSelector = createSelector(
      this.viewIncidentQueryService.mergedQuerySelector,
      this.assessmentRepository.selectors.resourcesSelector,
      this.currentIncidentSelector,
      this.currentIncidentByPlayerSelector,
      (
        queryParam,
        assessments: Assessment[],
        currentIncident: Incident,
        incidentsByPlayer: Incident[],
      ) => {
        const nextIncident = NextIncident(
          incidentsByPlayer,
          currentIncident.createdAt ?? '',
        );

        const clearanceAssessments = assessments
          .filter(
            (asx) =>
              asx.playerId === queryParam.selectedPlayerId &&
              (asx.assessment.assessmentType ===
                clinicAssessmentSpecs.info.identifier ||
                asx.assessment.assessmentType ===
                  gpClearanceAssessmentSpecs.info.identifier),
          )
          .sort((a: Assessment, b: Assessment) =>
            b?.createdAt?.localeCompare(a?.createdAt),
          );

        // if there is no next incident,
        // return all clearance assessments created after incident created date
        if (!nextIncident) {
          return clearanceAssessments.filter(
            (asx) => asx.createdAt >= currentIncident.createdAt,
          );
        }

        // if there is a next incident,
        // return all clearance assessments between this incident and next incident dates
        return clearanceAssessments.filter(
          (asx) =>
            asx.createdAt >= currentIncident.createdAt &&
            asx.createdAt < nextIncident.createdAt,
        );
      },
    );
  };
}
